import {
  AmenitiesAwningAwningIcon,
  Button,
  Dropdown,
  EButtonColorVariant,
  ETextStyleVariant,
  GeneralSearchIcon,
  LinkButton,
  RadioSelector,
  SelectorGroup,
  Text,
  VehiclesVehiclesCamperVanSimpleIcon,
  VehiclesVehiclesTravelTrailerSimpleIcon,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useInitialVehicleType } from '@/hooks/useInitialInputs';
import { EVehicleType, setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import {
  getHeaderSearchVehicleFilter,
  getHeaderSearchVehicleTypeFilter,
} from '@/redux/selectors/globalHeader';
import apiRequest from '@/services/apiRequest';
import { trackEvent } from '@/services/track-event';
import { IVehicle, IVehicleMake, IVehicleYear } from '@/services/types/search/vehicle';
import { getCoreApi } from '@/utility/getCoreApi';

import { FilterContent, TFilterContentSharedProps } from './FilterContent';

type TVehicleTypeFilterContentProps = TFilterContentSharedProps & {
  vehicleTypeOptions: Record<EVehicleType, string>;
};

export const VehicleTypeFilterContent = ({ onBack, ...props }: TVehicleTypeFilterContentProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { isAboveDesktop } = useBreakpoint();

  const vehicleType = useSelector(getHeaderSearchVehicleTypeFilter) || EVehicleType.ALL;
  const vehicle = useSelector(getHeaderSearchVehicleFilter);

  const { hasChanges, isInResetState, resetToInitial } = useInitialVehicleType(
    {
      vehicleType: vehicleType,
      vehicle: vehicle,
    },
    props.isOpened,
  );

  const handleVehicleTypeChange = (selection: EVehicleType) => {
    dispatch(
      setHeaderSearchFilter({
        vehicleType: selection,
      }),
    );

    trackEvent({
      event: 'search/filter/vehicle-type',
      action: 'update',
      before: vehicleType,
      after: selection,
    });
  };

  return (
    <FilterContent
      onBack={() => {
        const initialValue = resetToInitial();
        handleVehicleTypeChange(initialValue.vehicleType);
        onBack?.();
      }}
      header={intl.formatMessage({
        defaultMessage: 'Select a vehicle type',
        id: 'uTm+FB',
        description: 'Home Search Widget > Vehicle Type Modal Title',
      })}
      content={
        <div className="flex flex-col gap-10 lg:p-2">
          <SelectorGroup
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Select vehicle type',
              id: 'SBiBVJ',
              description: 'Home Search Widget > Vehicle Type Filter Aria Label',
            })}
            onChange={(value: string) => handleVehicleTypeChange(value as EVehicleType)}
            value={vehicleType}
            showIndicator={isAboveDesktop}>
            <div className="grid lg:grid-cols-4 gap-6">
              <RadioSelector
                ariaLabel={props.vehicleTypeOptions[EVehicleType.ALL]}
                label={props.vehicleTypeOptions[EVehicleType.ALL]}
                name={EVehicleType.ALL}
                value={EVehicleType.ALL}
                icon={GeneralSearchIcon}
                contentWrapperClassName="h-full"
                labelClassName="pr-1"
              />

              <RadioSelector
                ariaLabel={props.vehicleTypeOptions[EVehicleType.DRIVABLE]}
                label={props.vehicleTypeOptions[EVehicleType.DRIVABLE]}
                name={EVehicleType.DRIVABLE}
                value={EVehicleType.DRIVABLE}
                icon={VehiclesVehiclesCamperVanSimpleIcon}
                contentWrapperClassName="h-full"
              />

              <RadioSelector
                ariaLabel={props.vehicleTypeOptions[EVehicleType.TOWABLE]}
                label={props.vehicleTypeOptions[EVehicleType.TOWABLE]}
                name={EVehicleType.TOWABLE}
                value={EVehicleType.TOWABLE}
                icon={VehiclesVehiclesTravelTrailerSimpleIcon}
                contentWrapperClassName="h-full"
              />

              {!isAboveDesktop && vehicleType === EVehicleType.TOWABLE && <VehicleSelector />}

              <RadioSelector
                ariaLabel={props.vehicleTypeOptions[EVehicleType.DELIVERABLES]}
                label={props.vehicleTypeOptions[EVehicleType.DELIVERABLES]}
                name={EVehicleType.DELIVERABLES}
                value={EVehicleType.DELIVERABLES}
                icon={AmenitiesAwningAwningIcon}
                contentWrapperClassName="h-full"
              />
            </div>
          </SelectorGroup>

          {isAboveDesktop && vehicleType === EVehicleType.TOWABLE && <VehicleSelector />}
        </div>
      }
      footerContent={
        <FilterVehicleTypeFooter
          onClearVehicleType={() => {
            handleVehicleTypeChange(EVehicleType.ALL);
          }}
          onDone={() => {
            props.onDone?.();
          }}
          hasChanges={hasChanges}
          isInResetState={isInResetState}
        />
      }
      {...props}
    />
  );
};

const VehicleSelector = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const vehicle = useSelector(getHeaderSearchVehicleFilter);

  const [vehicleYear, setVehicleYear] = useState(vehicle?.year?.toString());
  const [vehicleMake, setVehicleMake] = useState(vehicle?.make);
  const [vehicleModel, setVehicleModel] = useState(vehicle?.id?.toString());

  const { data: vehicleYearsData } = useSWR(`${getCoreApi()}/towing_info`, url =>
    apiRequest<IVehicleYear[]>({ url }, true),
  );

  const { data: vehicleMakeData } = useSWR(
    vehicleYear ? `${getCoreApi()}/towing_info?year=${vehicleYear}` : null,
    url => apiRequest<IVehicleMake[]>({ url }, true),
  );

  const { data: vehicleModelData } = useSWR(
    vehicleYear && vehicleMake
      ? `${getCoreApi()}/towing_info?year=${vehicleYear}&make=${vehicleMake}`
      : null,
    url => apiRequest<IVehicle[]>({ url }, true),
  );

  const handleVehicleChange = (selection?: IVehicle) => {
    dispatch(
      setHeaderSearchFilter({
        vehicle: selection,
      }),
    );

    if (selection) {
      trackEvent({
        event: 'search/filter/vehicle',
        action: 'update',
        before: vehicle && `${vehicle.make}, ${vehicle.model}`,
        after: `${selection.make}, ${selection.model}`,
      });
    }
  };

  return (
    <div className="flex flex-col mb-6 gap-6 lg:mb-0">
      <Text variant={ETextStyleVariant.LargeRegular}>
        {intl.formatMessage({
          defaultMessage: 'Tell us about your towing vehicle to see what you can tow.',
          id: 'XgirlV',
        })}
      </Text>

      <div className="grid lg:grid-cols-3 gap-6">
        <DropdownSelector
          label={intl.formatMessage({ defaultMessage: 'Year', id: 'IFo1oo' })}
          inputValue={vehicleYear || ''}
          onChange={year => {
            setVehicleYear(year);
            setVehicleMake(undefined);
            setVehicleModel(undefined);
            if (!year) {
              handleVehicleChange(undefined);
            }
          }}
          options={vehicleYearsData?.map(({ year }) => ({
            value: year.toString(),
            label: year.toString(),
          }))}
        />

        <DropdownSelector
          label={intl.formatMessage({ defaultMessage: 'Make', id: '6AAM0P' })}
          inputValue={vehicleMake || ''}
          onChange={make => {
            setVehicleMake(make);
            setVehicleModel(undefined);
            if (!make) {
              handleVehicleChange(undefined);
            }
          }}
          options={vehicleMakeData?.map(({ make }) => ({
            value: make,
            label: make,
          }))}
          disabled={!vehicleYear}
          searchEnabled
        />

        <DropdownSelector
          label={intl.formatMessage({ defaultMessage: 'Model', id: 'rhSI1/' })}
          inputValue={vehicleModel || ''}
          onChange={model => {
            setVehicleModel(model);
            handleVehicleChange(
              model ? vehicleModelData?.find(({ id }) => id.toString() === model) : undefined,
            );
          }}
          options={vehicleModelData?.map(({ id, model }) => ({
            value: id.toString(),
            label: model,
          }))}
          disabled={!vehicleMake}
          searchEnabled
        />
      </div>
    </div>
  );
};

type TDropdownSelectorProps = {
  label: string;
  inputValue: string;
  onChange: (value: string) => void;
  options?: { value: string; label: string }[];
  disabled?: boolean;
  searchEnabled?: boolean;
};

const DropdownSelector = ({
  label,
  inputValue,
  onChange,
  options,
  disabled,
  searchEnabled = false,
}: TDropdownSelectorProps) => {
  const intl = useIntl();
  const searchPlaceholderText = intl.formatMessage(
    {
      defaultMessage: '{label} Search...',
      id: 'lWht0q',
    },
    { label },
  );

  return (
    <Dropdown
      label={label}
      ariaLabel={label}
      clearSelectionOptions={{
        ariaLabel: intl.formatMessage({
          defaultMessage: 'Clear selection',
          id: 'EYIw2M',
        }),
      }}
      inputValue={inputValue}
      onChange={onChange}
      disabled={disabled}
      searchEnabled={searchEnabled}
      searchPlaceholder={searchEnabled ? searchPlaceholderText : undefined}
      className="overflow-hidden"
      overlayClassName="home-search-widget-dropdown-overlay">
      {options?.map(option => (
        <Dropdown.Option
          key={option.value}
          value={option.value}
          content={{
            label: option.label,
          }}
        />
      )) || []}
    </Dropdown>
  );
};

type TFilterVehicleTypeFooterProps = {
  onClearVehicleType: () => void;
  onDone: () => void;
  hasChanges: boolean;
  isInResetState: boolean;
};

const FilterVehicleTypeFooter = ({
  onClearVehicleType,
  onDone,
  hasChanges,
  isInResetState,
}: TFilterVehicleTypeFooterProps) => {
  const intl = useIntl();
  const hasSingleAction = [onClearVehicleType, onDone].filter(x => x).length === 1;

  return (
    <div className="w-full">
      <div
        className={`flex items-center ${hasSingleAction ? 'justify-end' : 'justify-between'} p-4 lg:hidden`}>
        {onClearVehicleType && (
          <LinkButton
            label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
            onClick={onClearVehicleType}
            disabled={isInResetState}
          />
        )}
        {onDone && (
          <Button
            variant={EButtonColorVariant.Primary}
            label={intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })}
            onClick={onDone}
            disabled={!hasChanges}
          />
        )}
      </div>
    </div>
  );
};
